import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Settings,
  User,
  Bell,
  Shield,
  Palette,
  Globe,
  Database,
  Key,
  Mail,
  Smartphone,
  Monitor,
  Moon,
  Sun,
  Save,
  Eye,
  EyeOff,
  Trash2,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Lock,
  Unlock,
  Users,
  FileText,
  Zap,
  Cloud,
  Wifi,
  WifiOff,
  Clock,
  UserPlus,
  Edit,
  Search,
  Filter,
  X,
  Building2,
  Plus,
  Layers,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import NavMemoire from '../../components/memoire/NavMemoire';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import ToastContainer from '../../components/ui/ToastContainer';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import AutoSave from '../../components/ui/AutoSave';
import { useModal } from '../../hooks/useModal';
import { assistantsService } from '../../services/api/assistants';
import { useToast } from '../../hooks/useToast';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { settingsService } from '../../services/api/settings';
import { authManager } from '../../services/authManager';
import '../../styles/background-effects.css';
import { formatUseCaseCategoryLabel } from '../../utils/useCaseCategories';


const USECASE_CONTEXT_OPTIONS = [
  { value: 'support', label: 'Support de communication' },
  { value: 'brainstorming', label: 'Brainstorming' },
  { value: 'quantification', label: 'Quantification' },
  { value: 'analysis', label: 'Gestion documentaire' },
  { value: 'free_assisted', label: 'Elaboration de document' },
];

const buildCategoryFormState = (category?: any) => ({
  code: category?.code ?? '',
  name: category?.name ?? '',
  description: category?.description ?? '',
  icon: category?.icon ?? 'Layers',
  color: category?.color ?? 'teal',
  visible_in: Array.isArray(category?.visible_in) && category.visible_in.length > 0
    ? category.visible_in
    : USECASE_CONTEXT_OPTIONS.map((option) => option.value),
  order: category?.order ?? 0,
  is_active: category?.is_active ?? true,
});

const SettingsPage: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'profile' | 'users' | 'departments' | 'permissions' | 'document-types' | 'usecases'>('profile');
  const [useCasesTab, setUseCasesTab] = useState<'overview' | 'categories' | 'manage'>('overview');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showUserModal, setShowUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'user',
    department: '',
    license: 'basic'
  });

  // États pour l'édition
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editingPermission, setEditingPermission] = useState<any>(null);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [showEditPermissionModal, setShowEditPermissionModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // États pour la gestion des types de documents
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);

  // États pour la gestion des départements
  const [departments, setDepartments] = useState<any[]>([]);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [newDepartment, setNewDepartment] = useState({
    name: '',
    code: '',
    description: '',
    manager_name: ''
  });
  const [editingDepartment, setEditingDepartment] = useState<any>(null);

  // États pour la gestion des use cases
  const [useCases, setUseCases] = useState<any[]>([]);
  const [useCaseCategoriesList, setUseCaseCategoriesList] = useState<any[]>([]);
  const [showUseCaseModal, setShowUseCaseModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newUseCase, setNewUseCase] = useState({
    title: '',
    description: '',
    category: '',
    image: null as File | null,
    link: '',
    mode: 'lien',
    system_prompt: '',
    output_format: 'texte',
    is_active: true
  });
  const [editingUseCase, setEditingUseCase] = useState<any>(null);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryForm, setCategoryForm] = useState(buildCategoryFormState());
  const modal = useModal();
  const {
    toasts,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  } = useToast();

  const loadUseCaseCategories = useCallback(async () => {
    try {
      const categories = await settingsService.getUseCaseCategories();
      setUseCaseCategoriesList(categories);
      setNewUseCase((prev) => {
        if (prev.category || categories.length === 0) {
          return prev;
        }
        return { ...prev, category: categories[0].code };
      });
    } catch (error: any) {
      console.error('❌ Erreur chargement catégories use cases:', error);
      const errorMessage = error?.message || 'Erreur inconnue';
      showError('Erreur de chargement', `Impossible de charger les catégories: ${errorMessage}`);
      setUseCaseCategoriesList([]);
    }
  }, [showWarning]);

  const loadUseCases = useCallback(async () => {
    try {
      console.log('📋 Chargement des use cases depuis le backend...');
      const useCasesData = await settingsService.getUseCases();
      console.log('✅ Use cases chargés:', useCasesData.length);
      setUseCases(useCasesData);
    } catch (error: any) {
      console.error('❌ Erreur chargement use cases:', error);
      const errorMessage = error?.message || 'Erreur inconnue';
      showError('Erreur de chargement', `Impossible de charger les use cases: ${errorMessage}`);
      setUseCases([]);
    }
  }, [showWarning]);

  useEffect(() => {
    loadUseCaseCategories();
    loadUseCases();
  }, [loadUseCaseCategories, loadUseCases]);

  const isProfileOnly = location.pathname === '/workspace/profile';

  useEffect(() => {
    if (isProfileOnly) {
      setActiveTab('profile');
    }
  }, [isProfileOnly]);

  const contextLabelMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    USECASE_CONTEXT_OPTIONS.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, []);

  const getCategoryLabel = useCallback(
    (code?: string) => {
      if (!code) return 'Non catégorisé';
      const category = useCaseCategoriesList.find((cat: any) => cat.code === code);
      return category?.name || formatUseCaseCategoryLabel(code);
    },
    [useCaseCategoriesList]
  );

  const getCategoryColor = useCallback(
    (code?: string) => {
      if (!code) return 'slate';
      const category = useCaseCategoriesList.find((cat: any) => cat.code === code);
      return category?.color || 'slate';
    },
    [useCaseCategoriesList]
  );

  const CATEGORY_COLOR_CLASSES: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    teal: 'bg-teal-light/20 text-eg-accent border-teal-light/40',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    red: 'bg-eg-accent/10 text-red-700 border-red-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
    green: 'bg-eg-valide/10 text-green-700 border-green-100',
    pink: 'bg-pink-50 text-pink-700 border-pink-100',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-100',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const getCategoryBadgeClasses = useCallback(
    (code?: string) => {
      const color = getCategoryColor(code);
      return CATEGORY_COLOR_CLASSES[color] || CATEGORY_COLOR_CLASSES.slate;
    },
    [getCategoryColor]
  );

  const handleOpenCategoryModal = useCallback((category?: any) => {
    const base = category || { order: (useCaseCategoriesList?.length || 0) + 1 };
    setEditingCategory(category || null);
    setCategoryForm(buildCategoryFormState(base));
    setShowCategoryModal(true);
  }, [useCaseCategoriesList]);

  const handleCloseCategoryModal = useCallback(() => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryForm(buildCategoryFormState());
  }, []);

  const handleCategoryFieldChange = (field: string, value: any) => {
    setCategoryForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleCategoryContext = (context: string) => {
    setCategoryForm((prev) => {
      const current = new Set(prev.visible_in || []);
      if (current.has(context)) {
        current.delete(context);
      } else {
        current.add(context);
      }
      return { ...prev, visible_in: Array.from(current) };
    });
  };

  const handleSaveCategory = useCallback(async () => {
    try {
      const normalizedCode = (categoryForm.code || categoryForm.name || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/-+/g, '-');

      if (!normalizedCode) {
        showError('Code requis', 'Veuillez renseigner un code pour la catégorie.');
        return;
      }

      const payload = {
        ...categoryForm,
        code: normalizedCode,
        order: Number(categoryForm.order) || 0,
        visible_in: categoryForm.visible_in || [],
      };

      if (editingCategory) {
        await settingsService.updateUseCaseCategory(editingCategory.id, payload);
        showSuccess('Catégorie mise à jour', `${payload.name || payload.code} a été mise à jour.`);
      } else {
        await settingsService.createUseCaseCategory(payload);
        showSuccess('Catégorie créée', `${payload.name || payload.code} a été ajoutée.`);
      }

      handleCloseCategoryModal();
      await Promise.all([loadUseCaseCategories(), loadUseCases()]);
    } catch (error) {
      console.error('❌ Erreur sauvegarde catégorie:', error);
      showError('Erreur', 'Impossible de sauvegarder la catégorie. Réessayez.');
    }
  }, [categoryForm, editingCategory, handleCloseCategoryModal, loadUseCaseCategories, loadUseCases, showError, showSuccess]);

  const handleDeleteCategory = useCallback(
    async (category: any) => {
      if (!category) return;
      if (category.template_count > 0) {
        showError(
          'Suppression impossible',
          'Supprimez ou réassignez les use cases associés avant de supprimer cette catégorie.'
        );
        return;
      }

      const confirmed = window.confirm(`Supprimer la catégorie "${category.name}" ?`);
      if (!confirmed) return;

      try {
        await settingsService.deleteUseCaseCategory(category.id);
        showSuccess('Catégorie supprimée', `${category.name} a été supprimée.`);
        await loadUseCaseCategories();
      } catch (error) {
        console.error('❌ Erreur suppression catégorie:', error);
        showError('Erreur', 'Impossible de supprimer cette catégorie.');
      }
    },
    [loadUseCaseCategories, showError, showSuccess]
  );

  // Charger les types de documents depuis le backend
  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        console.log('📄 Chargement des types de documents depuis le backend...');
        const types = await settingsService.getDocumentTypes();
        const subtypes = await settingsService.getDocumentSubtypes();

        console.log('✅ Types chargés:', types.length);
        console.log('✅ Sous-types chargés:', subtypes.length);
        console.log('📋 Détails types:', types);
        console.log('📋 Détails sous-types:', subtypes);

        // Grouper les sous-types par type
        // Note: document_type peut être un objet {id, name, ...} ou juste un ID
        const formattedTypes = types.map((type: any) => {
          const typeSubtypes = subtypes.filter((st: any) => {
            // Gérer le cas où document_type est un objet ou un ID
            const subtypeTypeId = typeof st.document_type === 'object'
              ? st.document_type.id
              : st.document_type;
            return subtypeTypeId === type.id;
          }).map((st: any) => ({
            id: st.id,  // Garder l'ID pour pouvoir supprimer
            name: st.name
          }));

          console.log(`📁 Type "${type.name}" (ID: ${type.id}): ${typeSubtypes.length} sous-types`);
          typeSubtypes.forEach((st: any) => console.log(`   • ${st.name}`));

          return {
            id: type.id,
            name: type.name,
            subtypes: typeSubtypes  // Tableau d'objets avec id et name
          };
        });

        console.log('✅ Types formatés avec sous-types:', formattedTypes);
        setDocumentTypes(formattedTypes);
      } catch (error: any) {
        console.error('❌ Erreur chargement types documents:', error);
        const errorMessage = error?.message || 'Erreur inconnue';
        showError('Erreur de chargement', `Impossible de charger les types de documents: ${errorMessage}`);
        // Ne pas utiliser de données mockées - laisser la liste vide
        setDocumentTypes([]);
      }
    };

    loadDocumentTypes();
  }, []);

  // Charger les départements depuis le backend
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        console.log('🏢 Chargement des départements depuis le backend...');
        const depts = await settingsService.getDepartments();
        console.log('✅ Départements chargés:', depts.length);
        setDepartments(depts);
      } catch (error) {
        console.error('❌ Erreur chargement départements:', error);
        showError('Erreur', 'Erreur lors du chargement des départements');
      }
    };
    loadDepartments();
  }, []);

  const [isLoadingPermissionsMatrix, setIsLoadingPermissionsMatrix] = useState(false);
  const loadRolesAndPermissions = useCallback(async () => {
    setIsLoadingPermissionsMatrix(true);
    try {
      console.log('🔐 Chargement des rôles et capacités depuis le backend...');
      const matrix = await settingsService.getPermissionsMatrix();
      console.log('✅ Matrice reçue:', matrix);

      const rolesData = matrix.roles || [];
      const capabilitiesData = matrix.capabilities || [];

      console.log('📊 Rôles:', rolesData.length, 'Capacités:', capabilitiesData.length);

      const formattedRoles = rolesData.map((role: any) => ({
        ...role,
        id: role.id?.toString?.() || String(role.id),
        capabilityCodes: Array.isArray(role.capabilities)
          ? role.capabilities.map((cap: any) => cap.code || cap)
          : [],
      }));

      setRoles(formattedRoles);
      setPermissions(capabilitiesData);
      console.log('✅ Rôles et capacités chargés avec succès');
    } catch (error: any) {
      console.error('❌ Erreur chargement rôles/capacités:', error);

      // Afficher un message d'erreur clair au lieu d'utiliser des données mockées
      const errorMessage = error?.message || error?.response?.data?.detail || 'Erreur inconnue';
      const errorStatus = error?.response?.status;

      if (errorStatus === 403) {
        showError(
          'Permission refusée',
          'Vous n\'avez pas les permissions nécessaires pour accéder aux paramètres. Veuillez contacter un administrateur.'
        );
      } else if (errorStatus === 401) {
        showError(
          'Session expirée',
          'Votre session a expiré. Veuillez vous reconnecter.'
        );
      } else {
        showError(
          'Erreur de chargement',
          `Impossible de charger les permissions depuis le serveur: ${errorMessage}. Veuillez rafraîchir la page.`
        );
      }

      // Ne pas utiliser de données mockées - laisser les listes vides
      setPermissions([]);
      setRoles([]);
    } finally {
      setIsLoadingPermissionsMatrix(false);
    }
  }, [showError]);

  useEffect(() => {
    loadRolesAndPermissions();
  }, [loadRolesAndPermissions]);

  const [newDocumentType, setNewDocumentType] = useState({ name: '', subtypes: [] as string[] });
  const [newSubtype, setNewSubtype] = useState('');
  const [editingType, setEditingType] = useState<any>(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  // États de chargement
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [isSavingPermission, setIsSavingPermission] = useState(false);
  const [isSavingRole, setIsSavingRole] = useState(false);

  // Raccourcis clavier
  useKeyboardShortcuts([
    {
      key: 's',
      ctrlKey: true,
      callback: () => {
        if (activeTab === 'profile') {
          handleSaveProfile();
        }
      },
      description: 'Sauvegarder le profil (Ctrl+S)'
    },
    {
      key: 'p',
      ctrlKey: true,
      callback: () => {
        if (activeTab === 'profile') {
          handleChangePassword();
        }
      },
      description: 'Ouvrir le changement de mot de passe (Ctrl+P)'
    },
    {
      key: 'n',
      ctrlKey: true,
      callback: () => {
        if (activeTab === 'users') {
          handleAddUser();
        }
      },
      description: 'Ajouter un utilisateur (Ctrl+N)'
    },
    {
      key: 'Escape',
      callback: () => {
        if (showUserModal) setShowUserModal(false);
        if (showChangePasswordModal) handleCancelChangePassword();
        if (showEditPermissionModal) handleCancelEditPermission();
        if (showEditRoleModal) handleCancelEditRole();
      },
      description: 'Fermer les modales (Escape)'
    }
  ]);

  // États pour les paramètres
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    departmentId: null as number | null,
    position: '',
    timezone: 'Europe/Paris',
    language: 'fr'
  });

  // Charger le profil de l'utilisateur connecté au montage
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        // Charger depuis l'API backend pour avoir toutes les données
        const profileData = await settingsService.getCurrentUserProfile();
        console.log('👤 Profil utilisateur chargé:', profileData);

        setProfile({
          firstName: profileData.first_name || '',
          lastName: profileData.last_name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          company: profileData.department_name || 'Non assigné',
          departmentId: profileData.department_id || null,
          position: profileData.position || '',
          timezone: 'Europe/Paris',
          language: 'fr'
        });
      } catch (error) {
        console.error('Erreur chargement profil:', error);
        // Fallback sur authManager
        const currentUser = authManager.getUser();
        if (currentUser) {
          setProfile({
            firstName: currentUser.first_name || '',
            lastName: currentUser.last_name || '',
            email: currentUser.email || '',
            phone: '',
            company: currentUser.department_name || 'Non assigné',
            departmentId: currentUser.department_id || null,
            position: currentUser.position || '',
            timezone: 'Europe/Paris',
            language: 'fr'
          });
        }
      }
    };

    loadUserProfile();
  }, []);

  const [security, setSecurity] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    passwordExpiry: 90,
    loginNotifications: true,
    deviceTracking: true
  });

  const [notifications, setNotifications] = useState({
    email: {
      newDocuments: true,
      projectUpdates: true,
      systemAlerts: true,
      weeklyReports: false
    },
    push: {
      newMessages: true,
      deadlineReminders: true,
      systemUpdates: false
    },
    inApp: {
      taskAssignments: true,
      commentMentions: true,
      statusChanges: true
    }
  });

  const [appearance, setAppearance] = useState({
    theme: 'light',
    fontSize: 'medium',
    compactMode: false,
    animations: true,
    sidebarCollapsed: false
  });

  const [integrations, setIntegrations] = useState({
    googleDrive: { enabled: false, connected: false },
    dropbox: { enabled: false, connected: false },
    slack: { enabled: false, connected: false },
    microsoftTeams: { enabled: false, connected: false },
    zoom: { enabled: false, connected: false }
  });

  // Charger les connexions cloud depuis le backend
  useEffect(() => {
    const loadCloudConnections = async () => {
      try {
        console.log('☁️ Chargement des connexions cloud depuis le backend...');
        const connections = await settingsService.getCloudConnections();
        console.log('✅ Connexions cloud chargées:', connections.length);

        // Mettre à jour l'état des intégrations
        const newIntegrations = { ...integrations };
        connections.forEach((conn: any) => {
          const providerName = conn.provider?.name || '';
          if (providerName === 'google_drive') {
            newIntegrations.googleDrive = { enabled: true, connected: conn.is_active };
          } else if (providerName === 'dropbox') {
            newIntegrations.dropbox = { enabled: true, connected: conn.is_active };
          } else if (providerName === 'onedrive') {
            newIntegrations.microsoftTeams = { enabled: true, connected: conn.is_active };
          }
        });

        setIntegrations(newIntegrations);
      } catch (error) {
        console.error('Erreur chargement connexions cloud:', error);
        // Garder les valeurs par défaut
      }
    };

    loadCloudConnections();
  }, []);

  const [dataSettings, setDataSettings] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    dataRetention: 365,
    exportFormat: 'json',
    anonymizeData: false
  });

  // États pour la gestion des utilisateurs
  const [users, setUsers] = useState<any[]>([]);

  // Charger les utilisateurs depuis le backend
  useEffect(() => {
    const loadUsers = async () => {
      try {
        console.log('👥 Chargement des utilisateurs depuis le backend...');
        const usersData = await settingsService.getAllUsers();
        console.log('✅ Utilisateurs chargés:', usersData.length);

        // Transformer les données du backend au format attendu par l'UI
        const formattedUsers = usersData.map((user: any) => ({
          id: user.id.toString(),
          // Le compte sans nom affichait « undefined undefined » dans la
          // liste des comptes. Le repli visait « first_name » et
          // « last_name », que la liste des comptes ne sert pas : elle rend
          // un « full_name » déjà assemblé, vide quand le nom n'a pas été
          // saisi. On retombe donc sur l'identifiant, puis sur l'adresse,
          // qui désignent toujours quelqu'un.
          name: (user.full_name || '').trim() || user.username || user.email,
          email: user.email,
          role: user.role || 'user',
          status: user.is_active ? 'active' : 'inactive',
          lastLogin: user.last_login || user.updated_at || new Date().toISOString(),
          department: user.department_name || 'Non assigné',
          departmentId: user.department_id || null,  // Ajouter l'ID du département
          permissions: user.capabilities || [],  // Utiliser les capabilities réelles du backend
          modules: user.role === 'admin' ? ['documents', 'analytics', 'teams'] : ['documents'],
          license: user.role === 'admin' ? 'premium' : 'standard'
        }));

        setUsers(formattedUsers);
      } catch (error) {
        console.error('❌ Erreur chargement utilisateurs:', error);
        showWarning('Erreur de chargement', 'Impossible de charger les utilisateurs depuis le serveur');

        // Ne pas utiliser de données mockées pour éviter les confusions
        // L'utilisateur devra recharger la page ou vérifier sa connexion
        setUsers([]);
      }
    };

    loadUsers();
  }, []);

  // États pour les permissions et rôles
  const [permissions, setPermissions] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);

  // Fonctions pour la gestion des types de documents
  const handleAddDocumentType = async () => {
    if (newDocumentType.name.trim()) {
      try {
        console.log(`➕ Ajout du type "${newDocumentType.name}"`);

        // Appeler l'API backend pour créer le type
        const createdType = await settingsService.createDocumentType({
          name: newDocumentType.name.trim(),
          description: `${newDocumentType.name.trim()}`,
          color: '#3B82F6'  // Couleur par défaut (bleu)
        });

        // Créer les sous-types si fournis
        const createdSubtypes = [];
        for (const subtypeName of newDocumentType.subtypes) {
          if (subtypeName.trim()) {
            const subtype = await settingsService.createDocumentSubtype(
              createdType.id,
              subtypeName.trim()
            );
            createdSubtypes.push({ id: subtype.id, name: subtype.name });
          }
        }

        // Ajouter à l'état local
        const newType = {
          id: createdType.id,
          name: createdType.name,
          subtypes: createdSubtypes
        };

        setDocumentTypes([...documentTypes, newType]);
        setNewDocumentType({ name: '', subtypes: [] });
        setShowTypeModal(false);
        showSuccess('Type de document ajouté avec succès');
        console.log('✅ Type créé dans le backend');
      } catch (error: any) {
        console.error('❌ Erreur ajout type:', error);
        showError('Erreur', error.message || 'Impossible d\'ajouter le type');
      }
    }
  };

  const handleAddSubtype = async (typeId: number) => {
    if (newSubtype.trim()) {
      try {
        console.log(`➕ Ajout du sous-type "${newSubtype}" au type ID: ${typeId}`);

        // Appeler l'API backend pour créer le sous-type
        const createdSubtype = await settingsService.createDocumentSubtype(typeId, newSubtype.trim());

        // Mettre à jour l'état local après succès
        setDocumentTypes(prev => prev.map(type =>
          type.id === typeId
            ? {
              ...type,
              subtypes: [...type.subtypes, { id: createdSubtype.id, name: createdSubtype.name }]
            }
            : type
        ));

        setNewSubtype('');
        showSuccess('Sous-type ajouté avec succès');
        console.log('✅ Sous-type créé dans le backend');
      } catch (error: any) {
        console.error('❌ Erreur ajout sous-type:', error);
        showError('Erreur', error.message || 'Impossible d\'ajouter le sous-type');
      }
    }
  };

  const handleDeleteType = async (typeId: number) => {
    try {
      console.log(`🗑️ Suppression du type ID: ${typeId}`);

      // Appeler l'API backend pour supprimer
      await settingsService.deleteDocumentType(typeId);

      // Mettre à jour l'état local après succès
      setDocumentTypes(prev => prev.filter(type => type.id !== typeId));

      showSuccess('Type de document supprimé avec succès');
      console.log('✅ Type supprimé du backend');
    } catch (error: any) {
      console.error('❌ Erreur suppression type:', error);
      showError('Erreur', error.message || 'Impossible de supprimer le type');
    }
  };

  const handleDeleteSubtype = async (typeId: number, subtypeId: number) => {
    try {
      console.log(`🗑️ Suppression du sous-type ID: ${subtypeId}`);

      // Appeler l'API backend pour supprimer
      await settingsService.deleteDocumentSubtype(subtypeId);

      // Mettre à jour l'état local après succès
      setDocumentTypes(prev => prev.map(type =>
        type.id === typeId
          ? { ...type, subtypes: type.subtypes.filter((st: any) => st.id !== subtypeId) }
          : type
      ));

      showSuccess('Sous-type supprimé avec succès');
      console.log('✅ Sous-type supprimé du backend');
    } catch (error: any) {
      console.error('❌ Erreur suppression sous-type:', error);
      showError('Erreur', error.message || 'Impossible de supprimer le sous-type');
    }
  };

  // Fonctions pour gérer les use cases
  const handleAddUseCase = async () => {
    if (!newUseCase.title.trim() || !newUseCase.description.trim()) {
      showError('Champs requis', 'Le titre et la description sont obligatoires.');
      return;
    }
    // Un assistant intégré se définit par ses instructions, pas par un lien.
    if (newUseCase.mode === 'natif') {
      if (!newUseCase.system_prompt.trim()) {
        showError('Instructions requises', "Un assistant intégré a besoin d'instructions.");
        return;
      }
    } else if (!newUseCase.link.trim()) {
      showError('Lien requis', 'Indiquez le lien vers l\'outil externe.');
      return;
    }
    if (!newUseCase.category) {
      showError('Catégorie requise', 'Sélectionnez une catégorie avant d\'ajouter un use case.');
      return;
    }
    try {
      console.log(`➕ Ajout du use case "${newUseCase.title}"`);
      await settingsService.createUseCase(newUseCase);
      const defaultCategory = useCaseCategoriesList[0]?.code || '';
      setNewUseCase({
        title: '',
        description: '',
        category: defaultCategory,
        image: null,
        link: '',
        mode: 'lien',
        system_prompt: '',
        output_format: 'texte',
        is_active: true,
      });
      setShowUseCaseModal(false);
      showSuccess('Use case ajouté avec succès');
      await loadUseCases();
    } catch (error: any) {
      console.error('❌ Erreur ajout use case:', error);
      showError('Erreur', error.message || 'Impossible d\'ajouter le use case');
    }
  };

  const handleUpdateUseCase = async () => {
    if (!editingUseCase || !editingUseCase.title.trim() || !editingUseCase.description.trim()) {
      showError('Champs requis', 'Le titre et la description sont obligatoires.');
      return;
    }
    if (editingUseCase.mode === 'natif') {
      if (!(editingUseCase.system_prompt || '').trim()) {
        showError('Instructions requises', "Un assistant intégré a besoin d'instructions.");
        return;
      }
    } else if (!(editingUseCase.link || '').trim()) {
      showError('Lien requis', 'Indiquez le lien vers l\'outil externe.');
      return;
    }
    if (!editingUseCase.category) {
      showError('Catégorie requise', 'Sélectionnez une catégorie avant de sauvegarder.');
      return;
    }
    try {
      console.log(`✏️ Modification du use case "${editingUseCase.title}"`);
      await settingsService.updateUseCase(editingUseCase.id, editingUseCase);
      setEditingUseCase(null);
      setShowUseCaseModal(false);
      showSuccess('Use case modifié avec succès');
      await loadUseCases();
    } catch (error: any) {
      console.error('❌ Erreur modification use case:', error);
      showError('Erreur', error.message || 'Impossible de modifier le use case');
    }
  };

  const handleDeleteUseCase = async (useCaseId: number) => {
    try {
      console.log(`🗑️ Suppression du use case ID: ${useCaseId}`);
      await settingsService.deleteUseCase(useCaseId);
      setUseCases(prev => prev.filter(uc => uc.id !== useCaseId));
      showSuccess('Use case supprimé avec succès');
    } catch (error: any) {
      console.error('❌ Erreur suppression use case:', error);
      showError('Erreur', error.message || 'Impossible de supprimer le use case');
    }
  };

  const getStatusColor = (status: boolean) => {
    return status ? 'text-eg-valide bg-eg-valide/10' : 'text-eg-accent bg-eg-accent/10';
  };

  const getStatusIcon = (status: boolean) => {
    return status ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-eg-accent/10 text-eg-accent';
      case 'manager': return 'bg-teal-light/20 text-eg-accent';
      case 'user': return 'bg-eg-valide/10 text-eg-valide';
      case 'viewer': return 'bg-eg-voile text-eg-encre';
      default: return 'bg-eg-voile text-eg-encre';
    }
  };

  const getLicenseColor = (license: string) => {
    switch (license) {
      case 'premium': return 'bg-eg-ombre/20 text-eg-accent';
      case 'standard': return 'bg-teal-light/20 text-teal-light';
      case 'basic': return 'bg-eg-valide/10 text-eg-valide';
      default: return 'bg-eg-voile text-eg-encre';
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    try {
      console.log(`🔄 Toggle status utilisateur ID: ${userId}`);

      // Appeler l'API backend
      await settingsService.toggleUserStatus(userId);

      // Mettre à jour l'état local après succès
      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, status: user.status === 'active' ? 'inactive' : 'active' }
          : user
      ));

      const user = users.find(u => u.id === userId);
      const newStatus = user?.status === 'active' ? 'désactivé' : 'activé';
      showSuccess(`Utilisateur ${newStatus} avec succès`);
      console.log('✅ Status utilisateur mis à jour dans le backend');
    } catch (error: any) {
      console.error('❌ Erreur toggle status:', error);
      showError('Erreur', error.message || 'Impossible de modifier le statut');
    }
  };

  const handleResetUserPassword = (userId: string) => {
    const user = users.find(u => u.id === userId);
    const userLabel = user?.name || user?.email || 'cet utilisateur';
    modal.showConfirm(
      'Réinitialiser le mot de passe',
      `Confirmer la réinitialisation pour ${userLabel} ? Un mot de passe temporaire sera généré.`,
      async () => {
        try {
          const result = await settingsService.resetUserPassword(userId);
          const tempPassword = result?.temporary_password;
          if (tempPassword) {
            modal.showSuccess(
              'Mot de passe réinitialisé',
              `Mot de passe provisoire pour ${userLabel} :\n${tempPassword}\n\n`
              + (result?.courriel_envoye
                ? "Il vient de lui être envoyé par courriel."
                : "Aucun courriel n'a été envoyé — la messagerie n'est pas "
                  + "configurée. Notez-le et transmettez-le vous-même : il ne "
                  + "sera plus affiché.")
            );
          } else {
            showSuccess('Mot de passe réinitialisé', 'Un mot de passe temporaire a été généré.');
          }
        } catch (error: any) {
          const errorMessage = error?.message || 'Impossible de réinitialiser le mot de passe';
          showError('Erreur', errorMessage);
        }
      },
      'Réinitialiser'
    );
  };

  const handleAddUser = () => {
    setNewUser({
      name: '',
      email: '',
      role: 'user',
      department: '',
      license: 'basic'
    });
    setShowUserModal(true);
  };

  const handleSaveNewUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) {
      modal.showError(
        'Champs obligatoires',
        'Veuillez remplir tous les champs obligatoires'
      );
      return;
    }

    try {
      setIsSavingUser(true);
      console.log(`➕ Création utilisateur: ${newUser.email}`);

      // Séparer le nom en prénom et nom de famille
      const nameParts = newUser.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Appeler l'API backend pour créer l'utilisateur
      console.log('📤 Données envoyées:', {
        username: newUser.email.split('@')[0],
        email: newUser.email.trim(),
        first_name: firstName,
        last_name: lastName,
        role: newUser.role,
        department: newUser.department || null
      });

      const createdUser = await settingsService.createUser({
        username: newUser.email.split('@')[0],
        email: newUser.email.trim(),
        first_name: firstName,
        last_name: lastName,
        role: newUser.role,
        department: newUser.department || null,
        is_active: true
      });

      console.log('📥 Utilisateur créé:', createdUser);

      // Vérifier que l'utilisateur a bien été créé
      if (!createdUser) {
        throw new Error('La réponse du backend est invalide');
      }

      // Transformer pour l'UI
      const userToAdd = {
        id: (createdUser.id || Date.now()).toString(),
        name: createdUser.full_name || `${firstName} ${lastName}`,
        email: createdUser.email,
        role: createdUser.role || newUser.role,
        status: createdUser.is_active ? 'active' : 'inactive',
        lastLogin: createdUser.created_at || new Date().toISOString(),
        department: createdUser.department_name || 'Non assigné',
        permissions: newUser.role === 'admin' ? ['read', 'write', 'admin'] :
          newUser.role === 'manager' ? ['read', 'write'] : ['read'],
        modules: newUser.role === 'admin' ? ['documents', 'analytics', 'teams'] :
          newUser.role === 'manager' ? ['documents', 'content'] : ['documents'],
        license: newUser.license
      };

      setUsers([...users, userToAdd]);
      setShowUserModal(false);
      setNewUser({
        name: '',
        email: '',
        role: 'user',
        department: '',
        license: 'basic'
      });

      // Le message dit ce qui s'est passé, et rien de plus. Annoncer un envoi
      // qui n'a pas eu lieu laisse croire la personne prévenue : elle attend
      // un courriel qui ne viendra pas, et le mot de passe est perdu.
      modal.showSuccess(
        'Utilisateur créé',
        createdUser?.temporary_password
          ? `Mot de passe provisoire : ${createdUser.temporary_password}\n\n`
            + (createdUser?.courriel_envoye
              ? "Il vient de lui être envoyé par courriel."
              : "Aucun courriel n'a été envoyé — la messagerie n'est pas "
                + "configurée. Notez ce mot de passe et transmettez-le "
                + "vous-même : il ne sera plus affiché.")
          : "Le compte est créé, mais aucun mot de passe n'a été généré. "
            + "Utilisez « Réinitialiser le mot de passe » sur sa fiche."
      );
      console.log('✅ Utilisateur créé dans le backend');
    } catch (error: any) {
      console.error('❌ Erreur création utilisateur:', error);
      showError('Erreur', error.message || 'Impossible de créer l\'utilisateur');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleCancelNewUser = () => {
    setShowUserModal(false);
    setNewUser({
      name: '',
      email: '',
      role: 'user',
      department: 'IT',
      license: 'basic'
    });
  };

  // Fonctions pour l'édition des permissions
  const handleSavePermission = () => {
    if (editingPermission) {
      setPermissions(permissions.map(p =>
        p.id === editingPermission.id ? editingPermission : p
      ));
      setShowEditPermissionModal(false);
      setEditingPermission(null);
      modal.showSuccess(
        'Permission modifiée',
        `La permission "${editingPermission.name}" a été modifiée avec succès`
      );
    }
  };

  const handleCancelEditPermission = () => {
    setShowEditPermissionModal(false);
    setEditingPermission(null);
  };

  // Fonctions pour l'édition des rôles
  const handleSaveRole = () => {
    if (editingRole) {
      setRoles(roles.map(r =>
        r.id === editingRole.id ? editingRole : r
      ));
      setShowEditRoleModal(false);
      setEditingRole(null);
      modal.showSuccess(
        'Rôle modifié',
        `Le rôle "${editingRole.name}" a été modifié avec succès`
      );
    }
  };

  const handleCancelEditRole = () => {
    setShowEditRoleModal(false);
    setEditingRole(null);
  };

  // ============================================================================
  // GESTION DES DÉPARTEMENTS
  // ============================================================================

  const handleAddDepartment = async () => {
    if (!newDepartment.name.trim()) {
      showError('Erreur', 'Le nom du département est requis');
      return;
    }

    try {
      console.log('➕ Ajout d\'un nouveau département:', newDepartment.name);
      const createdDept = await settingsService.createDepartment(newDepartment);
      setDepartments([...departments, createdDept]);
      setNewDepartment({ name: '', code: '', description: '', manager_name: '' });
      setShowDepartmentModal(false);
      showSuccess('Département ajouté avec succès');
    } catch (error) {
      console.error('❌ Erreur ajout département:', error);
      showError('Erreur', 'Erreur lors de l\'ajout du département');
    }
  };

  const handleUpdateDepartment = async () => {
    if (!editingDepartment || !editingDepartment.name.trim()) {
      showError('Erreur', 'Le nom du département est requis');
      return;
    }

    try {
      console.log('✏️ Modification du département:', editingDepartment.id);
      const updatedDept = await settingsService.updateDepartment(editingDepartment.id, editingDepartment);
      setDepartments(departments.map(d => d.id === editingDepartment.id ? updatedDept : d));
      setEditingDepartment(null);
      showSuccess('Département mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur mise à jour département:', error);
      showError('Erreur', 'Erreur lors de la mise à jour du département');
    }
  };

  const handleDeleteDepartment = async (id: number) => {
    try {
      console.log('🗑️ Suppression du département:', id);
      await settingsService.deleteDepartment(id);
      setDepartments(departments.filter(d => d.id !== id));
      showSuccess('Département supprimé avec succès');
    } catch (error) {
      console.error('❌ Erreur suppression département:', error);
      showError('Erreur', 'Erreur lors de la suppression du département');
    }
  };

  const handleToggleDepartmentStatus = async (id: number) => {
    try {
      console.log('🔄 Toggle status département:', id);
      const result = await settingsService.toggleDepartmentStatus(id);
      setDepartments(departments.map(d => d.id === id ? { ...d, is_active: result.is_active } : d));
      showSuccess(result.message || 'Statut modifié');
    } catch (error) {
      console.error('❌ Erreur toggle status:', error);
      showError('Erreur', 'Erreur lors du changement de statut');
    }
  };

  // ============================================================================
  // GESTION DE LA MODIFICATION D'UTILISATEUR
  // ============================================================================

  const handleSaveEditUser = async () => {
    if (!editingUser) return;

    try {
      console.log('✏️ Modification de l\'utilisateur:', editingUser.id);

      // Préparer les données pour l'API
      const updateData: any = {
        first_name: editingUser.name.split(' ')[0] || '',
        last_name: editingUser.name.split(' ').slice(1).join(' ') || '',
        email: editingUser.email,
        role: editingUser.role
      };

      // Ajouter le département s'il est défini
      if (editingUser.departmentId) {
        updateData.department = editingUser.departmentId;
      }

      // Appeler l'API backend
      const updatedUser = await settingsService.updateUser(editingUser.id, updateData);

      // Mettre à jour la liste locale
      setUsers(users.map(u => u.id === editingUser.id ? {
        ...u,
        name: updatedUser.full_name || editingUser.name,
        email: updatedUser.email || editingUser.email,
        role: updatedUser.role || editingUser.role,
        department: updatedUser.department_name || 'Non assigné'
      } : u));

      setEditingUser(null);
      showSuccess('Utilisateur mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur modification utilisateur:', error);
      showError('Erreur', 'Erreur lors de la modification de l\'utilisateur');
    }
  };

  // Fonction de sauvegarde du profil
  const handleSaveProfile = async () => {
    setIsSavingProfile(true);

    try {
      console.log('💾 Sauvegarde du profil utilisateur...');

      // Préparer les données à envoyer
      const updateData: any = {
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        position: profile.position
      };

      // N'envoyer department que si un ID est défini
      if (profile.departmentId) {
        updateData.department = profile.departmentId;
      }

      // Appeler l'API backend pour mettre à jour le profil
      const updatedUser = await settingsService.updateUserProfile(updateData);

      // Mettre à jour l'utilisateur dans authManager avec les données du backend
      const currentUser = authManager.getUser();
      if (currentUser) {
        authManager.setUser({
          ...currentUser,
          first_name: updatedUser.first_name,
          last_name: updatedUser.last_name,
          full_name: updatedUser.full_name,
          email: updatedUser.email,
          department_id: updatedUser.department_id,
          department_name: updatedUser.department_name,
          position: updatedUser.position
        });
      }

      // Mettre à jour le state local avec les nouvelles données
      setProfile({
        ...profile,
        firstName: updatedUser.first_name || profile.firstName,
        lastName: updatedUser.last_name || profile.lastName,
        email: updatedUser.email || profile.email,
        phone: updatedUser.phone || profile.phone,
        company: updatedUser.department_name || 'Non assigné',
        departmentId: updatedUser.department_id || null,
        position: updatedUser.position || profile.position
      });

      // Le bandeau « Sauvegardé » n'était allumé que par la fausse
      // sauvegarde. Il suit désormais la vraie, et ne s'allume donc que
      // lorsque le serveur a bien enregistré.
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);

      showSuccess(
        'Profil mis à jour',
        'Vos informations personnelles ont été sauvegardées avec succès'
      );
      console.log('✅ Profil mis à jour dans le backend');
    } catch (error: any) {
      console.error('❌ Erreur sauvegarde profil:', error);
      showError(
        'Erreur de sauvegarde',
        error.message || 'Impossible de sauvegarder vos informations. Veuillez réessayer.'
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Fonctions pour le changement de mot de passe
  const handleChangePassword = () => {
    setShowChangePasswordModal(true);
  };

  const handleSavePassword = async () => {
    // Validation
    if (!passwordData.currentPassword.trim()) {
      showError(
        'Mot de passe actuel requis',
        'Veuillez saisir votre mot de passe actuel'
      );
      return;
    }

    if (!passwordData.newPassword.trim()) {
      showError(
        'Nouveau mot de passe requis',
        'Veuillez saisir un nouveau mot de passe'
      );
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showError(
        'Mot de passe trop court',
        'Le nouveau mot de passe doit contenir au moins 8 caractères'
      );
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError(
        'Mots de passe différents',
        'Le nouveau mot de passe et la confirmation ne correspondent pas'
      );
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      showError(
        'Même mot de passe',
        'Le nouveau mot de passe doit être différent de l\'actuel'
      );
      return;
    }

    setIsSavingPassword(true);

    try {
      // Le changement était simulé : une attente d'une seconde et demie, puis
      // un message de succès, sans qu'aucune requête ne parte. L'agent qui se
      // déconnectait découvrait que seul l'ancien mot de passe fonctionnait.
      await assistantsService.changerMotDePasse(
        passwordData.currentPassword,
        passwordData.newPassword,
      );

      setShowChangePasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      showSuccess(
        'Mot de passe modifié',
        'Utilisez-le à votre prochaine connexion.'
      );
    } catch (error: any) {
      showError(
        'Erreur de modification',
        error?.message || 'Impossible de modifier le mot de passe. Veuillez réessayer.'
      );
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleCancelChangePassword = () => {
    setShowChangePasswordModal(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleEditUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      console.log('✏️ Édition utilisateur:', user);
      setEditingUser(user);  // departmentId est déjà inclus
    }
  };

  const handleEditPermission = (permissionId: string) => {
    const permission = permissions.find(p => p.id === permissionId);
    if (permission) {
      setEditingPermission(permission);
      setShowEditPermissionModal(true);
    }
  };

  const handleEditRole = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      setEditingRole(role);
      setShowEditRoleModal(true);
    }
  };

  const handleTogglePermission = async (userId: string, capabilityId: string | number) => {
    // Trouver la capability correspondante pour obtenir son code
    const capability = permissions.find((p: any) => p.id === capabilityId || p.id?.toString() === capabilityId.toString());
    if (!capability) {
      console.error('Capability introuvable:', capabilityId);
      showError('Erreur', 'Capability introuvable');
      return;
    }

    const capabilityCode = capability.code || capability.id;
    if (!capabilityCode) {
      console.error('Code de capability introuvable:', capability);
      showError('Erreur', 'Code de capability introuvable');
      return;
    }

    // Mettre à jour l'état local immédiatement pour un feedback visuel
    const updatedUsers = users.map(user => {
      if (user.id === userId) {
        const hasCapability = user.permissions.includes(capabilityCode);
        return {
          ...user,
          permissions: hasCapability
            ? user.permissions.filter((p: string) => p !== capabilityCode)
            : [...user.permissions, capabilityCode]
        };
      }
      return user;
    });
    setUsers(updatedUsers);

    // Sauvegarder dans le backend
    try {
      const user = updatedUsers.find(u => u.id === userId);
      if (user) {
        await settingsService.updateUserCapabilities(userId, user.permissions);
        showSuccess('Permission mise à jour', `La permission "${capability.name}" a été ${user.permissions.includes(capabilityCode) ? 'accordée' : 'retirée'} avec succès`);
      }
    } catch (error: any) {
      console.error('Erreur sauvegarde permission:', error);
      // Revenir à l'état précédent en cas d'erreur
      setUsers(users);
      showError('Erreur', error.message || 'Impossible de sauvegarder la permission');
    }
  };

  const handleViewUsersWithPermission = (permissionId: string) => {
    const usersWithPermission = users.filter(user => user.permissions.includes(permissionId));
    const permission = permissions.find(p => p.id === permissionId);
    const userNames = usersWithPermission.map(u => u.name).join(', ');
    modal.showInfo(
      `Utilisateurs avec la permission "${permission?.name}"`,
      userNames || 'Aucun utilisateur'
    );
  };

  const handleEditRolePermissions = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      const availablePermissions = permissions.map(p => p.name).join(', ');
      modal.showInfo(
        `Rôle "${role.name}"`,
        `Permissions actuelles:\n${role.permissions.join(', ')}\n\nPermissions disponibles:\n${availablePermissions}`
      );
    }
  };

  // Filtrage des utilisateurs
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.department && user.department.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesStatus = filterStatus === 'all' || user.status === filterStatus;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, filterRole, filterStatus]);

  // Pagination des utilisateurs filtrés
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = useMemo(() => {
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, startIndex, endIndex]);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, filterStatus]);

  // Fonctions pour les boutons non implémentés
  const handleDisconnectDevice = (deviceName: string) => {
    modal.showConfirm(
      'Déconnecter l\'appareil',
      `Êtes-vous sûr de vouloir déconnecter ${deviceName} ?`,
      () => {
        modal.showSuccess(
          'Appareil déconnecté',
          `${deviceName} a été déconnecté avec succès`
        );
      }
    );
  };

  const handleExportData = () => {
    modal.showInfo(
      'Export des données',
      'Export de vos données en cours...\n\nFormat: JSON\nTaille estimée: 2.3 MB'
    );
  };

  const handleImportData = () => {
    modal.showInfo(
      'Import des données',
      'Fonctionnalité d\'import de données\n\nSélectionnez un fichier JSON, CSV ou Excel'
    );
  };

  const handleDeleteAccount = () => {
    modal.showDangerConfirm(
      'Supprimer le compte',
      'ATTENTION: Cette action est irréversible !\n\nÊtes-vous sûr de vouloir supprimer votre compte ?',
      () => {
        modal.showDangerConfirm(
          'Confirmation finale',
          'Dernière confirmation: Toutes vos données seront définitivement supprimées.',
          () => {
            modal.showSuccess(
              'Compte supprimé',
              'Votre compte a été supprimé avec succès'
            );
          },
          'Supprimer définitivement'
        );
      },
      'Supprimer le compte'
    );
  };

  const handleResetSettings = () => {
    modal.showConfirm(
      'Réinitialiser les paramètres',
      'Êtes-vous sûr de vouloir réinitialiser tous les paramètres ?',
      () => {
        modal.showSuccess(
          'Paramètres réinitialisés',
          'Tous les paramètres ont été réinitialisés aux valeurs par défaut'
        );
      }
    );
  };

  const handleEnableDeveloperMode = () => {
    modal.showSuccess(
      'Mode développeur activé',
      'Mode développeur activé !\n\nAccès aux outils de débogage et fonctionnalités expérimentales'
    );
  };

  const handleJoinBetaProgram = () => {
    modal.showSuccess(
      'Programme bêta',
      'Vous avez rejoint le programme bêta !\n\nVous recevrez les nouvelles fonctionnalités en avant-première'
    );
  };

  const handleConfigureAPI = () => {
    modal.showInfo(
      'Configuration API',
      'Configuration de l\'API\n\nGénérez vos clés API et configurez les webhooks'
    );
  };

  const handleIntegrationAction = (integrationKey: string, isConnected: boolean) => {
    const integrationName = integrationKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    if (isConnected) {
      modal.showInfo(
        `Configuration ${integrationName}`,
        `Configuration de ${integrationName}\n\nGérez vos paramètres de connexion`
      );
    } else {
      modal.showInfo(
        `Connexion ${integrationName}`,
        `Connexion à ${integrationName}\n\nRedirection vers l'authentification...`
      );
    }
  };

  return (
    <DashboardLayout>
      {/* Aurora Background Effect */}

      <div className="mx-auto max-w-[1100px] space-y-6 content-above-aurora">
        {!isProfileOnly && <NavMemoire actif="parametres" />}
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            {isProfileOnly ? (
              <h1 className="text-[26px] font-semibold tracking-tight text-eg-encre">Mon profil</h1>
            ) : (
              <h2 className="text-[18px] font-semibold tracking-tight text-eg-encre">Paramètres</h2>
            )}
            <p className="text-eg-gris">Configurez votre compte et vos préférences</p>
            <div className="flex items-center space-x-2 text-sm text-eg-gris mt-2">
              <span>💡</span>
              <span>Raccourcis: Ctrl+S (sauvegarder), Ctrl+P (mot de passe), Ctrl+N (nouvel utilisateur)</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {saved && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center space-x-2 text-eg-valide"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Sauvegardé</span>
              </motion.div>
            )}
            {/* Ce bouton attendait une seconde puis annonçait « Sauvegardé »
                sans qu'aucune requête ne parte. Le raccourci Ctrl+S annoncé
                juste au-dessus, lui, appelait la vraie sauvegarde : l'écran
                gardait donc les modifications de qui connaissait le raccourci
                et perdait celles de qui cliquait.

                Il ne s'affiche plus que sur l'onglet du profil, seul endroit
                où quelque chose se sauvegarde globalement. Les autres onglets
                enregistrent chaque élément à part, avec leur propre bouton. */}
            {activeTab === 'profile' && (
              <Button
                variant="primary"
                icon={<Save className="w-4 h-4" />}
                onClick={() => handleSaveProfile()}
                loading={isSavingProfile}
              >
                Sauvegarder
              </Button>
            )}
          </div>
        </div>

        {!isProfileOnly && (
          <div className="border-b border-eg-bord">
            <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-hide">
              {[
                { id: 'profile', name: 'Profil', icon: User },
                { id: 'users', name: 'Utilisateurs', icon: Users },
                { id: 'departments', name: 'Départements', icon: Building2 },
                // L'onglet « Permissions » est retiré : la matrice qu'il
                // affiche n'est appliquée nulle part. L'accès réel se joue sur
                // le rôle du compte et son appartenance à une équipe. Montrer
                // une grille de droits sans effet laisse croire qu'on y règle
                // quelque chose, et fait prendre pour acquis un cloisonnement
                // qui n'existe pas.
                { id: 'document-types', name: 'Documents', icon: FileText },
                { id: 'usecases', name: 'Use Cases', icon: Layers }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab.id
                    ? 'border-eg-accent text-eg-accent'
                    : 'border-transparent text-eg-gris hover:text-eg-accent hover:border-teal-light'
                    }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Contenu des onglets */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-eg-encre mb-4">Informations Personnelles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Prénom</label>
                    <input
                      type="text"
                      value={profile.firstName}
                      onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Nom</label>
                    <input
                      type="text"
                      value={profile.lastName}
                      onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Email</label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Département</label>
                    <select
                      value={profile.departmentId || ''}
                      onChange={(e) => {
                        const deptId = e.target.value ? parseInt(e.target.value) : null;
                        const dept = departments.find(d => d.id === deptId);
                        setProfile({
                          ...profile,
                          departmentId: deptId,
                          company: dept ? dept.name : 'Non assigné'
                        });
                      }}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Non assigné</option>
                      {departments.filter(d => d.is_active).map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Poste</label>
                    <input
                      type="text"
                      value={profile.position}
                      onChange={(e) => setProfile({ ...profile, position: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Fuseau Horaire</label>
                    <select
                      value={profile.timezone}
                      onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="Europe/London">Europe/London</option>
                      <option value="America/New_York">America/New_York</option>
                      <option value="Asia/Tokyo">Asia/Tokyo</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-eg-brun mb-2">Langue</label>
                    <select
                      value={profile.language}
                      onChange={(e) => setProfile({ ...profile, language: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="de">Deutsch</option>
                    </select>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="mt-6 pt-6 border-t border-eg-bord space-y-4">
                  {/* Sauvegarde du profil */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-eg-encre">Sauvegarde</h4>
                      <p className="text-sm text-eg-gris">Sauvegardez vos modifications de profil</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <AutoSave onSave={handleSaveProfile} />
                      <Button
                        variant="primary"
                        onClick={handleSaveProfile}
                        disabled={isSavingProfile}
                        icon={isSavingProfile ? <LoadingSpinner size="sm" /> : undefined}
                      >
                        {isSavingProfile ? 'Sauvegarde...' : 'Sauvegarder'}
                      </Button>
                    </div>
                  </div>

                  {/* Changement de mot de passe */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-eg-encre">Mot de passe</h4>
                      <p className="text-sm text-eg-gris">Modifiez votre mot de passe pour sécuriser votre compte</p>
                    </div>
                    <Button
                      variant="outline"
                      icon={<Key className="w-4 h-4" />}
                      onClick={handleChangePassword}
                    >
                      Modifier le mot de passe
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-eg-encre">Gestion des Utilisateurs</h3>
                  <Button variant="primary" icon={<UserPlus className="w-4 h-4" />} onClick={handleAddUser}>
                    Ajouter un utilisateur
                  </Button>
                </div>

                {/* Barre de recherche et filtres */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-eg-estompe" />
                    <input
                      type="text"
                      placeholder="Rechercher un utilisateur..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-eg-gris" />
                    <select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      className="px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Tous les rôles</option>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="user">User</option>
                    </select>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-eg-bord">
                    <thead className="bg-eg-papier">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Utilisateur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Rôle
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Licence
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Dernière connexion
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-eg-blanc divide-y divide-eg-bord">
                      {paginatedUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-eg-papier">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-teal-light/20 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-eg-accent" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-eg-encre">{user.name}</div>
                                <div className="text-sm text-eg-gris">{user.email}</div>
                                <div className="text-xs text-eg-estompe">{user.department}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 w-fit ${getStatusColor(user.status === 'active')}`}>
                              {getStatusIcon(user.status === 'active')}
                              {user.status === 'active' ? 'Actif' : 'Inactif'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLicenseColor(user.license)}`}>
                              {user.license}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-gris">
                            {new Date(user.lastLogin).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit className="w-4 h-4" />}
                                onClick={() => handleEditUser(user.id)}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Key className="w-4 h-4" />}
                                onClick={() => handleResetUserPassword(user.id)}
                              >
                                Réinit. MDP
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={user.status === 'active' ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                onClick={() => handleToggleUserStatus(user.id)}
                              >
                                {user.status === 'active' ? 'Désactiver' : 'Activer'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {filteredUsers.length > 0 && (
                  <div className="mt-6 flex items-center justify-between border-t border-eg-bord pt-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-eg-brun">Afficher</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-2 py-1 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-sm text-eg-brun">
                        sur {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-eg-brun">
                        Page {currentPage} sur {totalPages}
                      </span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="p-2 border border-[#DDE2DD] rounded-md hover:bg-eg-papier disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Page précédente"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="p-2 border border-[#DDE2DD] rounded-md hover:bg-eg-papier disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Page suivante"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {filteredUsers.length === 0 && (
                  <div className="mt-6 text-center py-8 text-eg-gris">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">Aucun utilisateur trouvé</p>
                  </div>
                )}
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'departments' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-eg-encre">Gestion des Départements</h3>
                  <Button
                    variant="primary"
                    icon={<Plus className="w-4 h-4" />}
                    onClick={() => setShowDepartmentModal(true)}
                  >
                    Ajouter un département
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-eg-bord">
                    <thead className="bg-eg-papier">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">Nom</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">Responsable</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">Utilisateurs</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">Statut</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-eg-gris uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-eg-blanc divide-y divide-eg-bord">
                      {departments.map((dept) => (
                        <tr key={dept.id} className="hover:bg-eg-papier">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Building2 className="w-5 h-5 text-eg-accent mr-3" />
                              <div className="text-sm font-medium text-eg-encre">{dept.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium bg-teal-100 text-eg-accent rounded-full">
                              {dept.code}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-gris">
                            {dept.manager_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-gris">
                            {dept.user_count || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleToggleDepartmentStatus(dept.id)}
                              className={`px-2 py-1 text-xs font-medium rounded-full ${dept.is_active
                                ? 'bg-eg-valide/10 text-eg-valide'
                                : 'bg-eg-voile text-eg-encre'
                                }`}
                            >
                              {dept.is_active ? 'Actif' : 'Inactif'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit className="w-4 h-4" />}
                                onClick={() => setEditingDepartment(dept)}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Trash2 className="w-4 h-4" />}
                                onClick={() => handleDeleteDepartment(dept.id)}
                              >
                                Supprimer
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-eg-encre mb-4">Gestion des Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {permissions.map((permission) => (
                    <div key={permission.id} className="p-4 border border-eg-bord rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-eg-encre">{permission.name}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLicenseColor(permission.level)}`}>
                          {permission.level}
                        </span>
                      </div>
                      <p className="text-sm text-eg-gris mb-3">{permission.description}</p>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2 whitespace-nowrap"
                          icon={<Edit className="w-4 h-4" />}
                          onClick={() => handleEditPermission(permission.id)}
                        >
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2 whitespace-nowrap"
                          icon={<Users className="w-4 h-4" />}
                          onClick={() => handleViewUsersWithPermission(permission.id)}
                        >
                          Utilisateurs
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-eg-encre mb-4">Rôles et Permissions</h3>
                <div className="space-y-4">
                  {roles.map((role) => (
                    <div key={role.id} className="p-4 border border-eg-bord rounded-md">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-eg-encre">{role.name}</h4>
                          <p className="text-sm text-eg-gris">{role.description}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(role.id)}`}>
                          {role.id}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {role.permissions && role.permissions.length > 0 ? role.permissions.map((permission: any) => (
                          <span key={permission} className="px-2 py-1 bg-teal-light/20 text-eg-accent text-xs rounded-full">
                            {permissions.find((p: any) => p.id === permission)?.name || permission}
                          </span>
                        )) : (
                          <span className="text-sm text-eg-gris">Aucune permission assignée</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2 whitespace-nowrap"
                          icon={<Edit className="w-4 h-4" />}
                          onClick={() => handleEditRole(role.id)}
                        >
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2 whitespace-nowrap"
                          icon={<Key className="w-4 h-4" />}
                          onClick={() => handleEditRolePermissions(role.id)}
                        >
                          Permissions
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-eg-encre mb-4">Matrice des Permissions</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-eg-bord">
                    <thead className="bg-eg-papier">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Utilisateur
                        </th>
                        {permissions.map((permission) => (
                          <th key={permission.id} className="px-6 py-3 text-center text-xs font-medium text-eg-gris uppercase tracking-wider">
                            {permission.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-eg-blanc divide-y divide-eg-bord">
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-eg-encre">
                            {user.name}
                          </td>
                          {permissions.map((permission) => {
                            // Utiliser le code de la capability pour la comparaison
                            const capabilityCode = permission.code || permission.id?.toString();
                            const hasPermission = user.permissions.includes(capabilityCode);
                            return (
                              <td
                                key={permission.id}
                                className="px-6 py-4 whitespace-nowrap text-center cursor-pointer hover:bg-eg-papier"
                                onClick={() => handleTogglePermission(user.id, permission.id)}
                                title={`Cliquer pour ${hasPermission ? 'retirer' : 'accorder'} la permission ${permission.name}`}
                              >
                                {hasPermission ? (
                                  <CheckCircle className="w-5 h-5 text-eg-valide mx-auto" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-eg-accent mx-auto" />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'document-types' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-eg-encre">Gestion des Types de Documents</h3>
                  <Button
                    variant="primary"
                    icon={<FileText className="w-4 h-4" />}
                    onClick={() => setShowTypeModal(true)}
                  >
                    Ajouter un type
                  </Button>
                </div>

                <div className="space-y-4">
                  {documentTypes.map((type) => (
                    <div key={type.id} className="bg-eg-blanc/60 border border-eg-bord rounded-md p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-eg-encre">{type.name}</h4>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Edit className="w-4 h-4" />}
                            onClick={() => setEditingType(type)}
                          >
                            Modifier
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Trash2 className="w-4 h-4" />}
                            onClick={() => handleDeleteType(type.id)}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-eg-brun mb-2">Sous-types</label>
                          <div className="space-y-2">
                            {type.subtypes && type.subtypes.map((subtype: any, index: number) => (
                              <div key={subtype.id || index} className="flex items-center justify-between bg-eg-papier rounded-md p-2">
                                <span className="text-sm text-eg-brun">{subtype.name || subtype}</span>
                                <button
                                  onClick={() => handleDeleteSubtype(type.id, subtype.id || index)}
                                  className="p-1.5 hover:bg-eg-accent/10 rounded-md transition-colors group"
                                  title="Supprimer ce sous-type"
                                  aria-label={`Supprimer ${subtype.name || subtype}`}
                                >
                                  <X className="w-4 h-4 text-eg-estompe group-hover:text-eg-accent transition-colors" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-eg-brun mb-2">Ajouter un sous-type</label>
                          <div className="flex space-x-2">
                            <input
                              type="text"
                              value={newSubtype}
                              onChange={(e) => setNewSubtype(e.target.value)}
                              placeholder="Nouveau sous-type"
                              className="flex-1 px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleAddSubtype(type.id)}
                              disabled={!newSubtype.trim()}
                            >
                              Ajouter
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'usecases' && (
          <div className="space-y-6">
            <div className="border-b border-eg-bord">
              <nav className="-mb-px flex flex-wrap gap-6">
                {[
                  { id: 'overview', name: 'Vue d\'ensemble' },
                  { id: 'categories', name: 'Catégories de Use Cases' },
                  { id: 'manage', name: 'Gestion des Use Cases' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setUseCasesTab(tab.id as typeof useCasesTab)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${useCasesTab === tab.id
                      ? 'border-eg-accent text-eg-accent'
                      : 'border-transparent text-eg-gris hover:text-eg-accent hover:border-teal-light'
                      }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {useCasesTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-eg-encre">Use Cases</h3>
                      <p className="text-sm text-eg-gris">
                        Organisez vos use cases par catégories et contrôlez leur visibilité.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUseCasesTab('categories')}
                      >
                        Voir les catégories
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setUseCasesTab('manage')}
                      >
                        Gérer les use cases
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-eg-bordure rounded-md p-4 bg-eg-blanc/70">
                      <p className="text-sm text-eg-gris">Catégories actives</p>
                      <p className="text-2xl font-semibold text-eg-encre mt-2">
                        {useCaseCategoriesList.filter((category: any) => category.is_active !== false).length}
                      </p>
                    </div>
                    <div className="border border-eg-bordure rounded-md p-4 bg-eg-blanc/70">
                      <p className="text-sm text-eg-gris">Use cases configurés</p>
                      <p className="text-2xl font-semibold text-eg-encre mt-2">
                        {useCases.length}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {useCasesTab === 'categories' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-eg-encre">Catégories de Use Cases</h3>
                      <p className="text-sm text-eg-gris">
                        Gérez les catégories, leurs couleurs et les interfaces où elles apparaissent.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      icon={<Plus className="w-4 h-4" />}
                      onClick={() => handleOpenCategoryModal()}
                    >
                      Nouvelle catégorie
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {useCaseCategoriesList.length === 0 && (
                      <div className="text-center py-10 text-eg-gris">
                        <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>Aucune catégorie configurée pour le moment.</p>
                        <p className="text-sm mt-1">
                          Créez votre première catégorie pour organiser vos use cases.
                        </p>
                      </div>
                    )}

                    {useCaseCategoriesList.map((category: any) => (
                      <div
                        key={category.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between border border-eg-bordure rounded-md p-4 bg-eg-blanc/70"
                      >
                        <div>
                          <div className="flex items-center space-x-3">
                            <span
                              className={`text-xs font-semibold px-3 py-1 rounded-full border ${getCategoryBadgeClasses(
                                category.code
                              )}`}
                            >
                              {category.name}
                            </span>
                            <span className="text-xs text-eg-estompe">/{category.code}</span>
                            {!category.is_active && (
                              <span className="text-xs text-red-500 font-medium">Inactif</span>
                            )}
                          </div>
                          {category.description && (
                            <p className="text-sm text-eg-gris mt-2">{category.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-3">
                            {category.visible_in?.map((context: string) => (
                              <span
                                key={context}
                                className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-teal-light/10 text-eg-accent border border-teal-light/30"
                              >
                                {contextLabelMap[context] || context}
                              </span>
                            ))}
                          </div>
                          <p className="mt-2 text-xs text-eg-gris">
                            {category.template_count || 0} use case(s)
                          </p>
                        </div>

                        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Edit className="w-4 h-4" />}
                            onClick={() => handleOpenCategoryModal(category)}
                          >
                            Modifier
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 className="w-4 h-4" />}
                            onClick={() => handleDeleteCategory(category)}
                            disabled
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            )}

            {useCasesTab === 'manage' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-eg-encre">Gestion des Use Cases</h3>
                    <Button
                      variant="primary"
                      icon={<Layers className="w-4 h-4" />}
                      onClick={() => {
                        const defaultCategory = useCaseCategoriesList[0]?.code || '';
                        setNewUseCase({ title: '', description: '', category: defaultCategory, image: null, link: '', mode: 'lien', system_prompt: '', output_format: 'texte', is_active: true });
                        setEditingUseCase(null);
                        setShowUseCaseModal(true);
                      }}
                      disabled={useCaseCategoriesList.length === 0}
                    >
                      Ajouter un Use Case
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {useCases.map((useCase: any) => (
                      <div key={useCase.id} className="bg-eg-blanc/60 border border-eg-bord rounded-md p-4 hover: transition-shadow">
                        {(useCase.image || useCase.image_url) && (
                          <img
                            src={useCase.image_url || useCase.image}
                            alt={useCase.title}
                            className="w-full h-32 object-cover rounded-md mb-3"
                          />
                        )}
                        <h4 className="font-semibold text-eg-encre mb-2">{useCase.title}</h4>
                        <p className="text-sm text-eg-gris mb-3 line-clamp-2">{useCase.description}</p>
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-xs px-2 py-1 rounded-full border ${getCategoryBadgeClasses(
                              useCase.category_code || useCase.category
                            )}`}
                          >
                            {useCase.category_name || getCategoryLabel(useCase.category_code || useCase.category)}
                          </span>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingUseCase({
                                  ...useCase,
                                  category: useCase.category_code || useCase.category,
                                });
                                setShowUseCaseModal(true);
                              }}
                              className="p-1.5 hover:bg-blue-50 rounded-md transition-colors"
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4 text-eg-estompe hover:text-blue-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteUseCase(useCase.id)}
                              className="p-1.5 hover:bg-eg-accent/10 rounded-md transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4 text-eg-estompe hover:text-eg-accent" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {useCases.length === 0 && (
                      <div className="col-span-full text-center py-12 text-eg-gris">
                        <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Aucun use case configuré</p>
                        <p className="text-sm mt-1">Créez votre premier use case pour commencer</p>
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            )}
          </div>
        )}

        {showCategoryModal && (
          <div className="fixed inset-0 z-[75] bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-eg-blanc rounded-md p-6 w-full max-w-lg shadow-xl overflow-y-auto"
              style={{ maxHeight: '90vh' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-eg-encre">
                  {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                </h3>
                <button onClick={handleCloseCategoryModal} className="text-eg-estompe hover:text-eg-gris">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-eg-brun mb-1">
                      Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={categoryForm.code}
                      onChange={(e) =>
                        handleCategoryFieldChange(
                          'code',
                          e.target.value
                            .toLowerCase()
                            .trim()
                            .replace(/[^a-z0-9-_]+/g, '-')
                            .replace(/-+/g, '-')
                        )
                      }
                      disabled={!!editingCategory}
                      placeholder="ex: redaction"
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-eg-papier disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-eg-brun mb-1">
                      Ordre d'affichage
                    </label>
                    <input
                      type="number"
                      value={categoryForm.order}
                      onChange={(e) => handleCategoryFieldChange('order', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-eg-brun mb-1">
                    Nom affiché <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => handleCategoryFieldChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: Rédaction"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-eg-brun mb-1">Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => handleCategoryFieldChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-eg-brun mb-1">Icône (Lucide)</label>
                    <input
                      type="text"
                      value={categoryForm.icon}
                      onChange={(e) => handleCategoryFieldChange('icon', e.target.value)}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Layers, FileText..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-eg-brun mb-1">Couleur</label>
                    <input
                      type="text"
                      value={categoryForm.color}
                      onChange={(e) => handleCategoryFieldChange('color', e.target.value)}
                      className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="blue, teal, purple..."
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-eg-brun mb-2">Visible dans</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {USECASE_CONTEXT_OPTIONS.map((option) => (
                      <label key={option.value} className="flex items-center space-x-2 text-sm text-eg-brun">
                        <input
                          type="checkbox"
                          checked={categoryForm.visible_in?.includes(option.value)}
                          onChange={() => handleToggleCategoryContext(option.value)}
                          className="w-3.5 h-3.5 text-eg-accent focus:ring-primary-500 border-[#DDE2DD] rounded"
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={categoryForm.is_active}
                      onChange={(e) => handleCategoryFieldChange('is_active', e.target.checked)}
                      className="w-3.5 h-3.5 text-eg-accent focus:ring-primary-500 border-[#DDE2DD] rounded"
                    />
                    <span className="text-xs font-medium text-eg-brun">Catégorie active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button variant="outline" size="sm" onClick={handleCloseCategoryModal}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveCategory}
                  disabled={!categoryForm.name.trim()}
                >
                  {editingCategory ? 'Enregistrer' : 'Créer'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Modal pour ajouter un utilisateur */}
        {showUserModal && (
          <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative bg-eg-blanc rounded-md p-6 w-full max-w-md shadow-xl overflow-y-auto"
              style={{ maxHeight: '90vh' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-eg-encre">Ajouter un utilisateur</h3>
                <button
                  onClick={handleCancelNewUser}
                  className="text-eg-estompe hover:text-eg-gris"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-eg-brun mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Jean Dupont"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-eg-brun mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: jean.dupont@solutionai.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-eg-brun mb-2">
                    Rôle
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-eg-brun mb-2">
                    Département
                  </label>
                  <select
                    value={newUser.department}
                    onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                    className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner un département</option>
                    {departments.filter(d => d.is_active).map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-eg-brun mb-2">
                    Licence
                  </label>
                  <select
                    value={newUser.license}
                    onChange={(e) => setNewUser({ ...newUser, license: e.target.value })}
                    className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="basic">Basic</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={handleCancelNewUser}>
                  Annuler
                </Button>
                <Button variant="primary" onClick={handleSaveNewUser}>
                  Ajouter
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Modal d'édition de permission */}
        {editingPermission && (
          <Modal
            isOpen={showEditPermissionModal}
            onClose={handleCancelEditPermission}
            title="Modifier la permission"
            size="md"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Nom de la permission
                </label>
                <input
                  type="text"
                  value={editingPermission.name}
                  onChange={(e) => setEditingPermission({ ...editingPermission, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Description
                </label>
                <textarea
                  value={editingPermission.description}
                  onChange={(e) => setEditingPermission({ ...editingPermission, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Niveau
                </label>
                <select
                  value={editingPermission.level}
                  onChange={(e) => setEditingPermission({ ...editingPermission, level: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="basic">Basique</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Organisation</option>
                </select>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button variant="outline" onClick={handleCancelEditPermission}>
                  Annuler
                </Button>
                <Button variant="primary" onClick={handleSavePermission}>
                  Sauvegarder
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal d'édition de rôle */}
        {editingRole && (
          <Modal
            isOpen={showEditRoleModal}
            onClose={handleCancelEditRole}
            title="Modifier le rôle"
            size="lg"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Nom du rôle
                </label>
                <input
                  type="text"
                  value={editingRole.name}
                  onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Description
                </label>
                <textarea
                  value={editingRole.description}
                  onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Permissions
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-eg-bord rounded-md p-3">
                  {permissions.map((permission) => (
                    <label key={permission.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editingRole.permissions.includes(permission.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingRole({
                              ...editingRole,
                              permissions: [...editingRole.permissions, permission.id]
                            });
                          } else {
                            setEditingRole({
                              ...editingRole,
                              permissions: editingRole.permissions.filter((p: string) => p !== permission.id)
                            });
                          }
                        }}
                        className="rounded border-[#DDE2DD] text-eg-accent focus:ring-teal-light"
                      />
                      <span className="text-sm text-eg-brun">{permission.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button variant="outline" onClick={handleCancelEditRole}>
                  Annuler
                </Button>
                <Button variant="primary" onClick={handleSaveRole}>
                  Sauvegarder
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal de changement de mot de passe */}
        <Modal
          isOpen={showChangePasswordModal}
          onClose={handleCancelChangePassword}
          title="Modifier le mot de passe"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Mot de passe actuel
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Saisissez votre mot de passe actuel"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Saisissez votre nouveau mot de passe"
              />
              <p className="text-xs text-eg-gris mt-1">Minimum 8 caractères</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Confirmer le nouveau mot de passe
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Confirmez votre nouveau mot de passe"
              />
            </div>
            <div className="flex items-center justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={handleCancelChangePassword}
                disabled={isSavingPassword}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleSavePassword}
                disabled={isSavingPassword}
                icon={isSavingPassword ? <LoadingSpinner size="sm" /> : undefined}
              >
                {isSavingPassword ? 'Modification...' : 'Modifier le mot de passe'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal personnalisé */}
        <Modal
          isOpen={modal.modalState.isOpen}
          onClose={modal.hideModal}
          title={modal.modalState.title}
          message={modal.modalState.message}
          type={modal.modalState.type}
          confirmText={modal.modalState.confirmText}
          cancelText={modal.modalState.cancelText}
          onConfirm={modal.modalState.onConfirm}
          onCancel={modal.modalState.onCancel}
          showCancel={modal.modalState.showCancel}
        />

        {/* Modal pour ajouter un nouveau type de document */}
        <Modal
          isOpen={showTypeModal}
          onClose={() => setShowTypeModal(false)}
          title="Ajouter un nouveau type de document"
          size="lg"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Nom du type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newDocumentType.name}
                onChange={(e) => setNewDocumentType(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Document Stratégiques"
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Sous-types (optionnel)
              </label>
              <div className="space-y-2">
                {newDocumentType.subtypes.map((subtype, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={subtype}
                      onChange={(e) => {
                        const newSubtypes = [...newDocumentType.subtypes];
                        newSubtypes[index] = e.target.value;
                        setNewDocumentType(prev => ({ ...prev, subtypes: newSubtypes }));
                      }}
                      placeholder="Ex: Plan d'activité Annuel"
                      className="flex-1 px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<X className="w-4 h-4" />}
                      onClick={() => {
                        const newSubtypes = newDocumentType.subtypes.filter((_, i) => i !== index);
                        setNewDocumentType(prev => ({ ...prev, subtypes: newSubtypes }));
                      }}
                    >
                      Supprimer
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setNewDocumentType(prev => ({ ...prev, subtypes: [...prev.subtypes, ''] }))}
                >
                  Ajouter un sous-type
                </Button>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowTypeModal(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleAddDocumentType}
                disabled={!newDocumentType.name.trim()}
              >
                Ajouter le type
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal pour ajouter/modifier un Use Case */}
        <Modal
          isOpen={showUseCaseModal || !!editingUseCase}
          onClose={() => {
            setShowUseCaseModal(false);
            setEditingUseCase(null);
            setNewUseCase({ title: '', description: '', category: 'redaction', image: null, link: '', mode: 'lien', system_prompt: '', output_format: 'texte', is_active: true });
          }}
          title={editingUseCase ? "Modifier le Use Case" : "Ajouter un nouveau Use Case"}
          size="md"
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-eg-brun mb-1">
                Titre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingUseCase?.title || newUseCase.title}
                onChange={(e) => editingUseCase
                  ? setEditingUseCase({ ...editingUseCase, title: e.target.value })
                  : setNewUseCase({ ...newUseCase, title: e.target.value })
                }
                placeholder="Ex: Document Standard"
                className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-eg-brun mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={editingUseCase?.description || newUseCase.description}
                onChange={(e) => editingUseCase
                  ? setEditingUseCase({ ...editingUseCase, description: e.target.value })
                  : setNewUseCase({ ...newUseCase, description: e.target.value })
                }
                placeholder="Description du template"
                className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-eg-brun mb-1">
                Catégorie <span className="text-red-500">*</span>
              </label>
              {useCaseCategoriesList.length === 0 ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-800">
                  Aucune catégorie disponible. Créez une catégorie avant d'ajouter un use case.
                </div>
              ) : (
                <select
                  value={editingUseCase?.category || newUseCase.category}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingUseCase) {
                      setEditingUseCase({ ...editingUseCase, category: value });
                    } else {
                      setNewUseCase({ ...newUseCase, category: value });
                    }
                  }}
                  className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-eg-blanc"
                >
                  {useCaseCategoriesList.map((category: any) => (
                    <option key={category.id} value={category.code}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-eg-gris">
                Les catégories contrôlent les interfaces (Support Com, Brainstorming...) où le use case est visible.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-eg-brun mb-1">
                Type de cas d'usage
              </label>
              <select
                value={(editingUseCase?.mode ?? newUseCase.mode) || 'lien'}
                onChange={(e) => editingUseCase
                  ? setEditingUseCase({ ...editingUseCase, mode: e.target.value })
                  : setNewUseCase({ ...newUseCase, mode: e.target.value })
                }
                className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-eg-blanc"
              >
                <option value="lien">Lien vers un outil externe</option>
                <option value="natif">Assistant intégré à la plateforme</option>
              </select>
              <p className="mt-1 text-xs text-eg-gris">
                Un assistant intégré s'ouvre dans OmnIA-EG : ni onglet externe, ni compte tiers,
                et l'usage est journalisé.
              </p>
            </div>

            {((editingUseCase?.mode ?? newUseCase.mode) || 'lien') === 'lien' ? (
              <div>
                <label className="block text-xs font-medium text-eg-brun mb-1">
                  Lien <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingUseCase?.link || newUseCase.link}
                  onChange={(e) => editingUseCase
                    ? setEditingUseCase({ ...editingUseCase, link: e.target.value })
                    : setNewUseCase({ ...newUseCase, link: e.target.value })
                  }
                  placeholder="https://chatgpt.com/g/..."
                  className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-eg-brun mb-1">
                    Instructions <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={10}
                    value={editingUseCase?.system_prompt || newUseCase.system_prompt || ''}
                    onChange={(e) => editingUseCase
                      ? setEditingUseCase({ ...editingUseCase, system_prompt: e.target.value })
                      : setNewUseCase({ ...newUseCase, system_prompt: e.target.value })
                    }
                    placeholder={"Décrivez le rôle de l'assistant, sa méthode et ses règles.\n\nExemple : « Tu assures la budgétisation des activités du PEV. Chaque coût unitaire doit être rattaché à une source identifiée... »"}
                    className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                  />
                  <p className="mt-1 text-xs text-eg-gris">
                    Ce texte n'est jamais montré aux utilisateurs. Vous pouvez y coller les
                    instructions d'un assistant existant.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-eg-brun mb-1">
                    Forme de la production
                  </label>
                  <select
                    value={(editingUseCase?.output_format ?? newUseCase.output_format) || 'texte'}
                    onChange={(e) => editingUseCase
                      ? setEditingUseCase({ ...editingUseCase, output_format: e.target.value })
                      : setNewUseCase({ ...newUseCase, output_format: e.target.value })
                    }
                    className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-eg-blanc"
                  >
                    <option value="texte">Réponse en texte libre</option>
                    <option value="document">Document — export Word</option>
                    <option value="tableau">Budget — grille modifiable, export Excel</option>
                    <option value="presentation">Présentation — export PowerPoint</option>
                  </select>
                  <p className="mt-1 text-xs text-eg-gris">
                    Le format « Budget » applique le barème officiel des coûts unitaires et
                    vérifie chaque ligne : code, source et calcul.
                  </p>
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-medium text-eg-brun mb-1">
                Image (optionnel)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    editingUseCase
                      ? setEditingUseCase({ ...editingUseCase, image: file })
                      : setNewUseCase({ ...newUseCase, image: file });
                  }
                }}
                className="w-full px-2.5 py-1.5 text-sm border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {editingUseCase?.image_url && (
                <img src={editingUseCase.image_url} alt="Current" className="mt-2 w-20 h-20 object-cover rounded-md" />
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editingUseCase ? editingUseCase.is_active : newUseCase.is_active}
                  onChange={(e) => {
                    if (editingUseCase) {
                      setEditingUseCase({ ...editingUseCase, is_active: e.target.checked });
                    } else {
                      setNewUseCase({ ...newUseCase, is_active: e.target.checked });
                    }
                  }}
                  className="w-3.5 h-3.5 text-eg-accent focus:ring-primary-500 border-[#DDE2DD] rounded"
                />
                <span className="text-xs font-medium text-eg-brun">
                  Actif (le use case sera visible dans l'interface)
                </span>
              </label>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowUseCaseModal(false);
                  setEditingUseCase(null);
                }}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={editingUseCase ? handleUpdateUseCase : handleAddUseCase}
                disabled={(
                  !newUseCase.title.trim()
                  || !newUseCase.description.trim()
                  || (newUseCase.mode === 'natif'
                    ? !newUseCase.system_prompt.trim()
                    : !newUseCase.link.trim())
                ) && !editingUseCase}
              >
                {editingUseCase ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal Ajouter Département */}
        <Modal
          isOpen={showDepartmentModal}
          onClose={() => {
            setShowDepartmentModal(false);
            setNewDepartment({ name: '', code: '', description: '', manager_name: '' });
          }}
          title="Ajouter un département"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Nom du département *
              </label>
              <input
                type="text"
                value={newDepartment.name}
                onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                placeholder="Ex: Ressources Humaines"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Code (facultatif)
              </label>
              <input
                type="text"
                value={newDepartment.code}
                onChange={(e) => setNewDepartment({ ...newDepartment, code: e.target.value })}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                placeholder="Ex: RH"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Responsable (facultatif)
              </label>
              <input
                type="text"
                value={newDepartment.manager_name}
                onChange={(e) => setNewDepartment({ ...newDepartment, manager_name: e.target.value })}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                placeholder="Ex: Jean Dupont"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-eg-brun mb-2">
                Description (facultatif)
              </label>
              <textarea
                value={newDepartment.description}
                onChange={(e) => setNewDepartment({ ...newDepartment, description: e.target.value })}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                rows={3}
                placeholder="Description du département"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDepartmentModal(false);
                  setNewDepartment({ name: '', code: '', description: '', manager_name: '' });
                }}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleAddDepartment}
                disabled={!newDepartment.name.trim()}
              >
                Ajouter
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal Modifier Utilisateur */}
        {editingUser && (
          <Modal
            isOpen={!!editingUser}
            onClose={() => setEditingUser(null)}
            title="Modifier l'utilisateur"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                  placeholder="Prénom Nom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Rôle
                </label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                >
                  <option value="user">Utilisateur</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Département
                </label>
                <select
                  value={editingUser.departmentId || ''}
                  onChange={(e) => {
                    const deptId = e.target.value ? parseInt(e.target.value) : null;
                    const dept = departments.find(d => d.id === deptId);
                    setEditingUser({
                      ...editingUser,
                      departmentId: deptId,
                      department: dept ? dept.name : 'Non assigné'
                    });
                  }}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                >
                  <option value="">Non assigné</option>
                  {departments.filter(d => d.is_active).map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setEditingUser(null)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveEditUser}
                  disabled={!editingUser.name.trim() || !editingUser.email.trim()}
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Modifier Département */}
        {editingDepartment && (
          <Modal
            isOpen={!!editingDepartment}
            onClose={() => setEditingDepartment(null)}
            title="Modifier le département"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Nom du département *
                </label>
                <input
                  type="text"
                  value={editingDepartment.name}
                  onChange={(e) => setEditingDepartment({ ...editingDepartment, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Code
                </label>
                <input
                  type="text"
                  value={editingDepartment.code}
                  onChange={(e) => setEditingDepartment({ ...editingDepartment, code: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Responsable
                </label>
                <input
                  type="text"
                  value={editingDepartment.manager_name || ''}
                  onChange={(e) => setEditingDepartment({ ...editingDepartment, manager_name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-eg-brun mb-2">
                  Description
                </label>
                <textarea
                  value={editingDepartment.description || ''}
                  onChange={(e) => setEditingDepartment({ ...editingDepartment, description: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-teal-dark"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setEditingDepartment(null)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleUpdateDepartment}
                  disabled={!editingDepartment.name.trim()}
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Toast Container */}
        <ToastContainer
          toasts={toasts}
          onRemoveToast={removeToast}
        />
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
